/**
 * iter170 — Dialog de changement de client sur un OR.
 * - Cherche un client existant par nom / téléphone
 * - OU crée un nouveau client (composé prénom+nom OU raison_sociale)
 * - À la confirmation : POST /or/{oid}/change-client
 * - Le véhicule reçoit le nouveau client dans son tableau client_ids[] (multi-propriétaires)
 */
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Search as SearchIcon, User, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function ChangeOrClientDialog({ orId, currentClientId, currentClientName, onClose, onChanged }) {
  const [mode, setMode] = useState("search"); // 'search' | 'new'
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newCli, setNewCli] = useState({ type: "particulier", prenom: "", nom: "", raison_sociale: "", telephone: "", email: "", adresse: "", code_postal: "", ville: "", pays: "CH" });
  const [saving, setSaving] = useState(false);

  const search = useCallback(async () => {
    if (!q || q.length < 2) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { q, limit: 20 } });
      setItems(data.items || data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(search, 250);
    return () => clearTimeout(t);
  }, [search]);

  const applyClient = async (client_id, name) => {
    setSaving(true);
    try {
      await api.post(`/or/${orId}/change-client`, { client_id });
      toast.success(`Client changé : ${name}`);
      onChanged?.(client_id, name);
      onClose();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const createAndApply = async () => {
    // Validation minimale
    if (newCli.type === "particulier" && !(newCli.prenom || newCli.nom)) {
      return toast.error("Renseignez au moins prénom ou nom");
    }
    if (newCli.type === "entreprise" && !newCli.raison_sociale) {
      return toast.error("Renseignez la raison sociale");
    }
    setSaving(true);
    try {
      // iter186 — Remap des clés vers le schéma backend attendu par POST /api/clients :
      //   telephone → tel_principal, code_postal → npa, ville → localite.
      const payload = {
        type: newCli.type,
        prenom: newCli.prenom || "",
        nom: newCli.nom || "",
        raison_sociale: newCli.raison_sociale || "",
        tel_principal: newCli.telephone || "",
        email: newCli.email || "",
        adresse: newCli.adresse || "",
        npa: newCli.code_postal || "",
        localite: newCli.ville || "",
        pays: newCli.pays || "CH",
      };
      const { data } = await api.post("/clients", payload);
      const created = data.client || data;
      const name = created.raison_sociale || `${created.prenom || ""} ${created.nom || ""}`.trim();
      await applyClient(created.id, name);
    } catch (e) {
      toast.error(`Création client échouée : ${e.response?.data?.detail || e.message}`);
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="change-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-[#52a675]" /> Changer le client de l&apos;OR
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-gray-600 mb-3">
          Client actuel : <b>{currentClientName || "—"}</b>. Le véhicule sera rattaché aux deux clients (multi-propriétaires historisés).
        </div>
        <div className="flex gap-2 mb-3">
          <Button size="sm" variant={mode === "search" ? "default" : "outline"} onClick={() => setMode("search")} data-testid="change-client-mode-search">
            <SearchIcon className="w-3.5 h-3.5 mr-1" /> Chercher
          </Button>
          <Button size="sm" variant={mode === "new" ? "default" : "outline"} onClick={() => setMode("new")} data-testid="change-client-mode-new">
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Nouveau client
          </Button>
        </div>

        {mode === "search" && (
          <div className="space-y-2">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nom, prénom, raison sociale ou téléphone…"
                className="pl-10"
                data-testid="change-client-search-input"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1">
              {loading && <div className="text-xs text-gray-400 text-center py-4"><Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" /> Recherche…</div>}
              {!loading && q && items.length === 0 && (
                <div className="text-xs text-gray-400 text-center py-4">Aucun client. Utilisez « Nouveau client ».</div>
              )}
              {items.map((c) => {
                const name = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
                const isCurrent = c.id === currentClientId;
                return (
                <button
                  key={c.id}
                  disabled={isCurrent || saving}
                  onClick={() => applyClient(c.id, name)}
                  className={`w-full text-left p-2 rounded border ${isCurrent ? "bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed" : "border-gray-100 hover:border-[#52a675] hover:bg-[#f0f9f4]"}`}
                  data-testid={`change-client-pick-${c.id}`}
                >
                  <div className="text-sm font-semibold">{name}{isCurrent && <Badge variant="outline" className="ml-2 text-[9px]">actuel</Badge>}</div>
                  <div className="text-[11px] text-gray-500">{c.telephone || "—"} · {c.ville || "—"}</div>
                </button>
              );})}
            </div>
          </div>
        )}

        {mode === "new" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button size="sm" variant={newCli.type === "particulier" ? "default" : "outline"} onClick={() => setNewCli({ ...newCli, type: "particulier" })}>Particulier</Button>
              <Button size="sm" variant={newCli.type === "entreprise" ? "default" : "outline"} onClick={() => setNewCli({ ...newCli, type: "entreprise" })}>Entreprise</Button>
            </div>
            {newCli.type === "entreprise" && (
              <div><Label className="text-xs">Raison sociale *</Label><Input value={newCli.raison_sociale} onChange={(e) => setNewCli({ ...newCli, raison_sociale: e.target.value })} data-testid="change-client-new-raison" /></div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Prénom{newCli.type === "particulier" && " *"}</Label><Input value={newCli.prenom} onChange={(e) => setNewCli({ ...newCli, prenom: e.target.value })} data-testid="change-client-new-prenom" /></div>
              <div><Label className="text-xs">Nom{newCli.type === "particulier" && " *"}</Label><Input value={newCli.nom} onChange={(e) => setNewCli({ ...newCli, nom: e.target.value })} data-testid="change-client-new-nom" /></div>
              <div><Label className="text-xs">Téléphone</Label><Input value={newCli.telephone} onChange={(e) => setNewCli({ ...newCli, telephone: e.target.value })} data-testid="change-client-new-tel" /></div>
              <div><Label className="text-xs">Email</Label><Input value={newCli.email} onChange={(e) => setNewCli({ ...newCli, email: e.target.value })} data-testid="change-client-new-email" /></div>
              {/* iter186 — Adresse rue + numéro (nécessaire pour la facturation, QR-bill etc.) */}
              <div className="col-span-2"><Label className="text-xs">Adresse (rue + n°)</Label><Input value={newCli.adresse} onChange={(e) => setNewCli({ ...newCli, adresse: e.target.value })} placeholder="Rue de Genève 15" data-testid="change-client-new-adresse" /></div>
              <div><Label className="text-xs">NPA</Label><Input value={newCli.code_postal} onChange={(e) => setNewCli({ ...newCli, code_postal: e.target.value })} data-testid="change-client-new-npa" /></div>
              <div><Label className="text-xs">Ville</Label><Input value={newCli.ville} onChange={(e) => setNewCli({ ...newCli, ville: e.target.value })} data-testid="change-client-new-ville" /></div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          {mode === "new" && (
            <Button onClick={createAndApply} disabled={saving} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="change-client-create-apply">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Créer & attribuer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
